import React from 'react';
import { useTranslation } from 'react-i18next';
import { HiDownload, HiUpload, HiRefresh, HiExclamation } from 'react-icons/hi';
import { MaterialTransaction, MaterialTransactionType, MaterialTransactionStatus } from '@/types/material';

interface MaterialStockCardTabProps {
  materialCode: string;
  transactions: MaterialTransaction[];
}

const MaterialStockCardTab: React.FC<MaterialStockCardTabProps> = ({
  materialCode: _materialCode,
  transactions,
}) => {
  const { t } = useTranslation();

  const getTransactionTypeBadge = (type: MaterialTransactionType) => {
    const typeConfig = {
      import: { color: 'bg-green-100 text-green-800', label: t('materials.transactionTypes.import'), icon: <HiDownload className="h-4 w-4" /> },
      export: { color: 'bg-red-100 text-red-800', label: t('materials.transactionTypes.export'), icon: <HiUpload className="h-4 w-4" /> },
      return: { color: 'bg-blue-100 text-blue-800', label: t('materials.transactionTypes.return'), icon: <HiRefresh className="h-4 w-4" /> },
      adjustment: { color: 'bg-yellow-100 text-yellow-800', label: t('materials.transactionTypes.adjustment'), icon: <HiExclamation className="h-4 w-4" /> },
    };

    const config = typeConfig[type];
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  const getStatusBadge = (status: MaterialTransactionStatus) => {
    const statusConfig = {
      completed: { color: 'bg-green-100 text-green-800', label: t('materials.transactionStatus.completed') },
      pending: { color: 'bg-yellow-100 text-yellow-800', label: t('materials.transactionStatus.pending') },
      cancelled: { color: 'bg-red-100 text-red-800', label: t('materials.transactionStatus.cancelled') },
    };

    const config = statusConfig[status];
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  const handleExportExcel = () => {
    // TODO: Implement Excel export
    console.log('Export to Excel');
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Lịch sử giao dịch</h3>
        <button
          onClick={handleExportExcel}
          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <HiDownload className="h-4 w-4 mr-2" />
          {t('materials.stockTab.exportExcel')}
        </button>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {transactions.map((transaction) => (
            <li key={transaction.id} className="px-4 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    {getTransactionTypeBadge(transaction.transactionType)}
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">
                      {transaction.documentNo}
                    </div>
                    <div className="text-sm text-gray-500">
                      {transaction.supplierName && `Nhà cung cấp: ${transaction.supplierName}`}
                    </div>
                    <div className="text-sm text-gray-500">
                      {transaction.notes}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">
                      {transaction.quantity} {transaction.unitPrice ? 'x' : ''} {formatCurrency(transaction.unitPrice)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatCurrency(transaction.totalAmount)}
                    </div>
                  </div>
                  <div className="flex flex-col items-end space-y-1">
                    {getStatusBadge(transaction.status)}
                    <div className="text-xs text-gray-500">
                      {formatDateTime(transaction.createdAt)}
                    </div>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {transactions.length === 0 && (
        <div className="text-center py-8">
          <HiRefresh className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Chưa có lịch sử giao dịch</h3>
          <p className="mt-1 text-sm text-gray-500">
            Lịch sử giao dịch sẽ được hiển thị khi backend API được cập nhật.
          </p>
          <p className="mt-1 text-xs text-gray-400">
            Tính năng này đang được phát triển.
          </p>
        </div>
      )}
    </div>
  );
};

export default MaterialStockCardTab;
